import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Link,
  Stack,
  IconButton,
  InputAdornment,
  Checkbox,
  Grid,
  TextField,
  Button,
  FormHelperText,
  Snackbar,
  Alert,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import * as Yup from 'yup';
import axios from 'axios';
import { toast } from 'react-toastify';

import { ErrorMessage, Field, Form, Formik, useField } from 'formik';
import Iconify from '../../../components/iconify';
import secureStorage from '../../../utils/secureStorage';

// ----------------------------------------------------------------------

function ErrorSnackBar(props) {
  return (
    <>
      <Snackbar
        open={props.open}
        onClose={(event, reason) => {
          props.setOpenError(false);
        }}
        {...props}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {props.message}
        </Alert>
      </Snackbar>
      <Snackbar onClose={() => props.setOpenError(false)} />
    </>
  );
}

export default function ResetForm() {
  const navigate = useNavigate();

  useEffect(() => {
    if (secureStorage.getItem('reset_token') === null) {
      toast.error('Invalid request');
      navigate('/login');
    }
  }, []);

  const [loading, setLoading] = useState(false);

  const [openError, setOpenError] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <Formik
      initialValues={{
        password: '',
        confirmPassword: '',
      }}
      validationSchema={Yup.object({
        password: Yup.string()
          .required('Required')
          .test(
            'not-temp-password',
            'Self-chosen password cannot start with KK.',
            (value) => !value || !/^kk/i.test(value.trim())
          ),
        confirmPassword: Yup.string()
          .oneOf([Yup.ref('password'), null], 'Passwords must match')
          .required('Confirm Password is required'),
      })}
      onSubmit={(values, { setSubmitting }) => {
        setLoading(true);
        axios({
          method: 'post',
          url: `${process.env.REACT_APP_API_URL}reset`,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${secureStorage.getItem('reset_token')}`,
          },
          data: values,
        })
          .then((res) => {
            // if(res.data)
            secureStorage.removeItem('reset_token');
            if (res.data.reset === 'OK') {
              navigate('/', { replace: true });
            } else {
              setOpenError('something went wrong, try again later');
            }
          })
          .catch((e) => {
            setOpenError(e.response.data.error);
          })
          .finally(() => {
            setLoading(false);
          });
        setSubmitting(false);
      }}
    >
      {({ values, setFieldValue }) => (
        <Form>
          <Stack spacing={3}>
            <Field
              component={TextField}
              fullWidth
              onChange={(e) => setFieldValue('password', e.target.value)}
              defaultValue={values.password}
              name="password"
              label="password"
              variant="outlined"
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Field
              component={TextField}
              fullWidth
              onChange={(e) => setFieldValue('confirmPassword', e.target.value)}
              defaultValue={values.confirmPassword}
              name="confirmPassword"
              label="Confirm Password"
              variant="outlined"
              type={showConfirmPassword ? 'text' : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      <Iconify icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <ErrorMessage render={(msg) => <FormHelperText error>{msg}</FormHelperText>} name="password" />
            <ErrorMessage render={(msg) => <FormHelperText error>{msg}</FormHelperText>} name="confirmPassword" />
          </Stack>

          <LoadingButton loading={loading} sx={{ mt: 3 }} fullWidth size="large" type="submit" variant="contained">
            Reset Password
          </LoadingButton>
          <ErrorSnackBar
            message={openError}
            open={typeof openError === 'string'}
            setOpenError={setOpenError}
            severity="error"
          />
        </Form>
      )}
    </Formik>
  );
}
